import { gql } from "@apollo/client";
import { myDynosFields, productCategoryFields } from "../fields";

export const USER_PUBLISHER_ROLE = gql`
  query EdShopifyUserAndPublisher($where: EdShopifyUserAndPublisherWhereInput!) {
    edShopifyUserAndPublisher(where: $where) {
      user {
        id
        firstName
        lastName
        email
        phone
        isActive
      }
      publisher {
        id
        publisherName
        logoPath
        videoPath
        about
        accountType
        domainName
        additionalLink
        owner {
          id
          firstName
          lastName
          email
        }
        stripeAccountId
        stripeOnboardingUrl
        otherInfo
        instagram
        facebook
        linkedin
        x
        seoTitle
        seoDescription
        subscriptionInfo
        creditsAvailable
        extraCreditsAvailable
        creditsAvailableUntil
      }
      role {
        isStaff
        isAdmin
        isOwner
      }
    }
  }
`;
export const PUBLISHER_DATA = gql`
  query EdShopifyPublisher($where: PublisherWhereUniqueInput!) {
    edShopifyPublisher(where: $where) {
        id
        publisherName
        additionalAdmins {
          id
          firstName
          lastName
          email
        }
        staff {
          id
          firstName
          lastName
          email
        }
        bookPublishers {
          id
        }
        instructors {
          id
          InstructorName
          InstructorBio
          InstructorPic
        }
        Coupon {
          id
          couponExpiryDate
          couponCode
          pricingPlanType
          isActive
          otherInfo
          maxRedemption
          percentageOff
          appliesToProduct {
            id
            productName
          }
        }
        catalogs {
          id
        }
      }
    }
`;

// Marketing Analytics
export const MARKETING_OVERVIEW = gql`
  query MarketingOverview($publisherId: String!, $from: DateTime, $to: DateTime, $campaign: String) {
    marketingOverview(publisherId: $publisherId, from: $from, to: $to, campaign: $campaign) {
      visits
      views
      buys
      viewCTR
      buyCTR
      topReferrers { source count }
      topCampaigns { name count }
      funnelByProduct { productId productName views buys ctr }
    }
  }
`;

export const TIME_SERIES = gql`
  query TimeSeries($publisherId: String!, $from: DateTime, $to: DateTime, $interval: String) {
    timeSeries(publisherId: $publisherId, from: $from, to: $to, interval: $interval) {
      bucket
      visits
      views
      buys
    }
  }
`;

export const TOP_KEYWORDS = gql`
  query TopKeywords($publisherId: String!, $limit: Int, $from: DateTime, $to: DateTime) {
    topKeywords(publisherId: $publisherId, limit: $limit, from: $from, to: $to) {
      keyword
      count
    }
  }
`;

// Revenue Analytics
export const REVENUE_OVERVIEW = gql`
  query RevenueOverview($publisherId: String!, $from: DateTime, $to: DateTime, $limitTop: Int) {
    revenueOverview(publisherId: $publisherId, from: $from, to: $to, limitTop: $limitTop) {
      purchases
      periodRevenue
      allTimeRevenue
      topProducts { productId productName purchases revenue }
    }
  }
`;

export const REVENUE_SERIES = gql`
  query RevenueSeries($publisherId: String!, $from: DateTime, $to: DateTime, $interval: String) {
    revenueSeries(publisherId: $publisherId, from: $from, to: $to, interval: $interval) {
      bucket
      purchases
      revenue
    }
  }
`;

export const PURCHASES_SERIES = gql`
  query PurchasesSeries($publisherId: String!, $from: DateTime, $to: DateTime, $interval: String) {
    purchasesSeries(publisherId: $publisherId, from: $from, to: $to, interval: $interval) {
      bucket
      purchases
      revenue
    }
  }
`;

export const REVENUE_DASHBOARD = gql`
  query RevenueDashboard(
    $publisherId: String!,
    $from: DateTime,
    $to: DateTime,
    $interval: String,
    $limitTop: Int
  ) {
    revenueOverview(publisherId: $publisherId, from: $from, to: $to, limitTop: $limitTop) {
      purchases
      periodRevenue
      allTimeRevenue
      topProducts { productId productName purchases revenue }
    }
    revenueSeries(publisherId: $publisherId, from: $from, to: $to, interval: $interval) {
      bucket
      purchases
      revenue
    }
    purchasesSeries(publisherId: $publisherId, from: $from, to: $to, interval: $interval) {
      bucket
      purchases
      revenue
    }
  }
`;

// Combined dashboard query to reduce network roundtrips
export const MARKETING_DASHBOARD = gql`
  query MarketingDashboard(
    $publisherId: String!,
    $from: DateTime,
    $to: DateTime,
    $interval: String,
    $limit: Int
  ) {
    marketingOverview(publisherId: $publisherId, from: $from, to: $to) {
      visits
      views
      buys
      viewCTR
      buyCTR
      topReferrers { source count }
      topCampaigns { name count }
      funnelByProduct { productId productName views buys ctr }
    }
    timeSeries(publisherId: $publisherId, from: $from, to: $to, interval: $interval) {
      bucket
      visits
      views
      buys
    }
    topKeywords(publisherId: $publisherId, limit: $limit, from: $from, to: $to) {
      keyword
      count
    }
  }
`;
// Drafts: courses/books in progress for this publisher
export const EDSHOPIFY_DRAFTS = gql`
  query EdshopifyDrafts($publisherId: String!, $limit: Int, $offset: Int) {
    edshopifyDrafts(publisherId: $publisherId, limit: $limit, offset: $offset) {
      id
      productName
      productType
      productStatus
      isAddedToStore
      updatedAt
      catalogId
    }
  }
`;

export const EDSHOPIFY_DRAFTS_COUNT = gql`
  query EdshopifyDraftsCount($publisherId: String!) {
    edshopifyDraftsCount(publisherId: $publisherId)
  }
`;
// Engagement Analytics
export const ENGAGEMENT_SUMMARY = gql`
  query EngagementSummary($publisherId: String!, $productIds: [String!], $from: DateTime, $to: DateTime) {
    engagementSummary(publisherId: $publisherId, productIds: $productIds, from: $from, to: $to) {
      activeLearners
      avgSessionSeconds
      totalCompletions
    }
  }
`;

export const ENGAGEMENT_COURSES = gql`
  query EngagementCourses($publisherId: String!, $productIds: [String!], $from: DateTime, $to: DateTime, $skip: Int, $take: Int) {
    engagementCourses(publisherId: $publisherId, productIds: $productIds, from: $from, to: $to, skip: $skip, take: $take) {
      totalCount
      items {
        productId
        productName
        avgCompletionPct
        completionsCount
      }
    }
  }
`;

export const ENGAGEMENT_COURSE_DETAIL = gql`
  query EngagementCourseDetail($productId: String!, $from: DateTime, $to: DateTime) {
    engagementCourseDetail(productId: $productId, from: $from, to: $to) {
      productId
      productName
      avgCompletionPct
      completionsCount
      modules {
        moduleId
        moduleName
        avgCompletionPct
        edDocs {
          dynoId
          dynoTitle
          dynoTypeName
          timeSpentSeconds
          watchTimeSeconds
          practiceCount
          avgScore
        }
      }
    }
  }
`;

export const CATEGORIES_GRADES_SUBJECTS = gql`
  query DynoCategories {
    dynoCategories(where: {
    parentDynoCategoryId: null
    }) {
      id
      name
      parentCategory {
        id
        name
        parentCategory {
          id
          name
        }
      }
    }
    dynoGrades {
      id
      name
    }
    dynoSubjects {
      id
      name
    }
  }
`
export const SUB_CATEGORIES = gql`
  query DynoCategories($where: DynoCategoryWhereInput!) {
    dynoCategories(where: $where) {
      id
      name
    }
  }
`
export const GRADES_SUBJECTS = gql`
  query DynoCategories {
    dynoGrades {
      id
      name
    }
    dynoSubjects {
      id
      name
    }
  }
`

export const DYNOCATEGORIES = gql`
  query DynoCategories($where: DynoCategoryWhereInput) {
    dynoCategories(where: $where) {
      id
      name
      parentCategory {
        id
        name
        parentCategory {
          id
          name
        }
      }
    }
  }
`;

export const PRODUCT = gql`
  query Product($where: ProductWhereUniqueInput!) {
    product(where: $where) {
      id
      productName
      productType
      productHeading
      productDescription
      productStatus
      productPricingType
      productCoverPath
      isAddedToStore
      productContent {
        dynoList(take: 1){
          _count {
            dynosUnderList
          }
        }
      }
      productAdmins {
        id
        firstName
        lastName
        email
      }
      productCategory {
        id
        name
        parentCategory {
          id
          name
        }
      }
      productGrades {
        id
        name
      }
      productSubjects {
        id
        name
      }
      productSubscriptionPlans {
        id
        planName
        planShortDescription
        price
        currency
        frequency
      }
      productOneTimePlans {
        id
        planName
        price
        currency
        planShortDescription
        planLongDescription
      }
      bookPublisher {
        id
        PublisherName
        PublisherBio
        PublisherPic
      }
      courseInstructor {
        id
        InstructorName
        InstructorBio
        InstructorPic
      }
    }
  }
`;
export const PRODUCTDETAILS = gql`
  query Product($where: ProductWhereUniqueInput!) {
    product(where: $where) {
      id
      productName
      productType
      productHeading
      productDescription
      productStatus
      productPricingType
      productCoverPath
      isAddedToStore
      productAdmins {
        id
        firstName
        lastName
        email
      }
      productCategory {
        id
        name
        parentCategory {
          id
          name
        }
      }
      productGrades {
        id
        name
      }
      productSubjects {
        id
        name
      }
      productSubscriptionPlans {
        id
        planName
        planShortDescription
        price
        currency
        frequency
      }
      productOneTimePlans {
        id
        planName
        price
        currency
        planShortDescription
        planLongDescription
      }
      bookPublisher {
        id
        PublisherName
        PublisherBio
        PublisherPic
      }
      courseInstructor {
        id
        InstructorName
        InstructorBio
        InstructorPic
      }
    }
  }
`;

export const CATALOG = gql`
  query Catalog($where: CatalogWhereUniqueInput!) {
    catalog(where: $where) {
      id
      catalogAdmins {
        id
        firstName
        lastName
        email
      }
    }
  }
`;

export const DYNO_GRADES = gql`
  query DynoGradesSubjects {
    dynoGrades {
      id
      name
    }
  }
`;

export const DYNO_SUBJECTS = gql`
  query DynoSubjects {
    dynoSubjects {
      id
      name
    }
  }
`;

export const PUBLISHERCATALOG = gql`
  query Publisher($where: PublisherWhereUniqueInput!) {
    publisher(where: $where) {
      catalogs(orderBy: { index: asc }) {
        id
        index
        catalogName
        catalogStatus
        catalogAdmins {
          id
          firstName
          lastName
          email
        }
        products {
          id
          productName
          productStatus
          productPricingType
        }
      }
    }
  }
`;

export const CHECKDOMAINNAME = gql`
  query EdshopifyCheckDomainName($where: edshopifyCheckDomainNameWhereInput!) {
    edshopifyCheckDomainName(where: $where)
  }
`;

export const CATALOG_DETAILS = gql`
  query Catalog($where: CatalogWhereUniqueInput!) {
    catalog(where: $where) {
      id
      catalogName
      heading
      subHeading
      description
      catalogAdmins {
        id
        firstName
        lastName
        email
      }
      catalogStatus
    }
  }
`;

export const CATALOG_PRODUCTS = gql`
  query Catalog(
    $where: CatalogWhereUniqueInput!
    $take: Int
    $skip: Int
  ) {
    catalog(where: $where) {
      id
      catalogName
      catalogAdmins { #Needed to update UI as per role in product page
        id
        firstName
        lastName
        email
      }
      products(orderBy: { updatedAt: desc }, take: $take, skip: $skip) {
        id
        productName
        productStatus
        isAddedToStore
        productType
        productAdmins {
          id
          firstName
          lastName
          email
        }
      }
    }
  }
`;
export const PRODUCTS_CONNECTION = gql`
    query ProductsConnection(
        $where: ProductWhereInput
        $first: Int
        $skip: Int
    ) {
      productsConnection(orderBy: { updatedAt: desc }, where: $where, first: $first, skip: $skip) {
            pageInfo {
                hasNextPage
                endCursor
            }
            edges {
                node {
                  id
                  productName
                  productStatus
                  isAddedToStore
                  productType
                  productAdmins {
                    id
                    firstName
                    lastName
                    email
                  }
                }
            }
            count
        }
    }
`;
export const MEMBERS_LIST = gql`
  query EdshopifyPublisherMembers($where: PublisherWhereUniqueInput!) {
    edshopifyPublisherMembers(where: $where) {
      id
      owner {
        id
        firstName
        lastName
        email
      }
      additionalAdmins {
        id
        firstName
        lastName
        email
        isActive
      }
      staff {
        id
        firstName
        lastName
        email
        isActive
      }
    }
  }
`;

export const INSTRUCTORS = gql`
  query Instructors {
    instructors {
      id
      InstructorName
      InstructorBio
      InstructorPic
    }
  }
`;

export const PRODUCT_CONTENT = gql`
  query Product($where: ProductWhereUniqueInput!) {
    product(where: $where) {
      id
      productName
      productStatus
      productType
      productPricingType
      productDescription
      productCoverPath
      productGrades {
        id
        name
      }
      productSubjects {
        id
        name
      }
      productCategory {
        ${productCategoryFields}
      }
      productSubscriptionPlans {
        id
      }
      productOneTimePlans {
        id
      }
      isAddedToStore
      productContent {
        id
        dynoList(orderBy: {listIndex: asc}) {
          id
          listIndex
          listName
          listTitle
          dynosUnderList(orderBy: {index: asc}) {
            id
            index
            dyno {
              ${myDynosFields}
            }
            isLocked
          }
        }
      }
      productAdmins {
        id
        firstName
        lastName
        email
      }
      catalog {
        id
        catalogAdmins {
          id
        }
      }
    }
  }
`;

export const PUBLISHERCATALOGS = gql`
  query Publisher($where: PublisherWhereUniqueInput!) {
    publisher(where: $where) {
      catalogs(orderBy: { index: asc }) {
        id
        index
        catalogName
        catalogAdmins {
          id
        }
      }
    }
  }
`;

export const PUBLISHER_CATALOG_PRODUCTS = gql`
  query Catalog($where: CatalogWhereUniqueInput!) {
    catalog(where: $where) {
      id
      products(
        where: { productStatus: { equals: Published } }
        orderBy: { index: asc }
      ) {
        id
        productName
        productStatus
      }
    }
  }
`;

export const PRODUCT_DYNOS = gql`
  query Product($where: ProductWhereUniqueInput!) {
    product(where: $where) {
      id
      productName
      productContent {
        id
        dynoList(orderBy: { listIndex: asc }) {
          id
          listName
          listTitle
          dynosUnderList(orderBy: { index: asc }) {
            id
            dyno {
              id
              dynoTitle
              dynoType {
                id
                name
              }
            }
          }
        }
      }
    }
  }
`;

export const DYNOESCONNECTIONPUBLISHER = gql`
    query DynoesConnectionPublisher(
        $where: DynoWhereInput
        $first: Int
        $orderBy: [DynoOrderByWithRelationInput]
        $publisherId: String!
        $isCount: Boolean
    ) {
        dynoesConnectionPublisher(where: $where, first: $first, orderBy: $orderBy, publisherId: $publisherId, isCount: $isCount) {
            pageInfo {
                hasNextPage
                endCursor
            }
            edges {
                node {
                    ${myDynosFields}
                }
            }
        }
    }
`;

export const PUBLISHERDYNOESCOUNT = gql`
  query DynoesConnectionPublisher($publisherId: String!, $isCount: Boolean) {
    dynoesConnectionPublisher(publisherId: $publisherId, isCount: $isCount) {
      count
    }
  }
`;

export const DYNOESCOUNT = gql`
  query DynoesCount($where: DynoWhereInput) {
    findDynoCount(where: $where)
  }
`;

export const DYNOES = gql`
    query Dynoes(
        $take: Int
        $skip: Int
        $orderBy: [DynoOrderByWithRelationInput]
        $where: DynoWhereInput
    ) {
        dynoes(where: $where, take: $take, skip: $skip orderBy: $orderBy) {
          ${myDynosFields}
          productReferences {
            id
            productName
            catalog {
              id
              catalogName
            }
          }
        }
    }
`;
export const DYNOES1 = gql`
    query Dynoes(
        $take: Int
        $skip: Int
        $orderBy: [DynoOrderByWithRelationInput]
        $where: DynoWhereInput
    ) {
        dynoes(where: $where, take: $take, skip: $skip orderBy: $orderBy) {
          id
          dynoTitle
          dynoType {
              id
              name
          }
        }
    }
`;
export const RECENT_DYNO = gql`
    query Dynoes(
        $take: Int
        $skip: Int
        $orderBy: [DynoOrderByWithRelationInput]
        $where: DynoWhereInput
    ) {
        dynoes(where: $where, take: $take, skip: $skip orderBy: $orderBy) {
          id
          dynoTitle
          createdAt
          updatedAt
          dynoType {
              id
              name
          }
        }
    }
`;

export const MYDYNOES = gql`
    query Dynoes(
        $take: Int
        $skip: Int
        $orderBy: [DynoOrderByWithRelationInput]
        $where: DynoWhereInput
    ) {
        dynoes(where: $where, take: $take, skip: $skip orderBy: $orderBy) {
          id
          dynoTitle
          dynoType {
            id
            name
          }
        }
    }
`;

export const CATALOGS_LIST = gql`
  query Catalogs($where: PublisherWhereUniqueInput!) {
    publisher(where: $where) {
      id
      catalogs(where: { catalogStatus: { not: { equals: Published } } }) {
        id
        catalogName
      }
    }
  }
`;

export const PRODUCT_LIST = gql`
  query ProductList($where: CatalogWhereUniqueInput!) {
    catalog(where: $where) {
      products(where: { productStatus: { not: { equals: Published } } }) {
        id
        productName
      }
    }
  }
`;

export const PRODUCT_DYNO_LIST = gql`
  query ProductDynoList($where: ProductWhereUniqueInput!) {
    product(where: $where) {
      productContent {
        id
        dynoList {
          id
          listName
          dynosUnderList(orderBy: { index: desc }, take: 1) {
            index
          }
        }
      }
    }
  }
`;

export const DYNOTYPES = gql`
  query DynoTypes {
    dynoTypes {
      id
      name
    }
  }
`;

export const DYNOSTATUS = gql`
  query DynoStatuses {
    dynoStatuses {
      id
      statusType
    }
  }
`;

export const LOCALE = gql`
  query Locales {
    locales {
      id
      name
    }
  }
`;

export const LOCALEDATE = gql`
  query LocaleDates {
    localeDates {
      id
      name
    }
  }
`;

export const GETTOPICPERSUBJECTGRADE = gql`
  query GetTagsAsPersubjectGrade($where: GetTagsAsPersubjectGradeWhereInput!) {
    getTagsAsPersubjectGrade(where: $where) {
      id
      tag
      listName
      fillInTheBlanksCount
      mcqCount
    }
  }
`;

export const EDSHOP_IMAGE_RESOURCES = gql`
  query EdshopifyImageResources($where: EdshopifyResourcesWhereInput!) {
    edshopifyImageResources(where: $where) {
      resources {
        key
        url
        type
        name
        size
        uuid
      }
      hasMore
    }
  }
`;
export const EDSHOP_AUDIO_RESOURCES = gql`
  query EdshopifyAudioResources($where: EdshopifyResourcesWhereInput!) {
    edshopifyAudioResources(where: $where) {
      resources {
        key
        url
        type
        name
        size
        uuid
      }
      hasMore
    }
  }
`;
export const EDSHOP_PDF_RESOURCES = gql`
  query EdshopifyPdfResources($where: EdshopifyResourcesWhereInput!) {
    edshopifyPdfResources(where: $where) {
      resources {
        key
        url
        type
        name
        size
        uuid
      }
      hasMore
    }
  }
`;

export const EDSHOP_PPT_RESOURCES = gql`
  query EdshopifyPptResources($where: EdshopifyResourcesWhereInput!) {
    edshopifyPptResources(where: $where) {
      resources {
        key
        url
        type
        name
        size
        uuid
      }
      hasMore
    }
  }
`;

export const EDSHOP_GENERATE_PRESIGNED_URL = gql`
  query EdshopifyGeneratePresignedUrl($where: EdshopifyGeneratePresignedUrlWhereInput!) {
    edshopifyGeneratePresignedUrl(where: $where) {
      key
      url
    }
  }
`;

export const EDSHOP_VIDEOS = gql`
  query EdshopifyVideos($where: EdshopifyVideosWhereInput!) {
    edshopifyVideos(where: $where) {
      videos {
        id
        url
        name
        duration
        thumbnail
        description
        tags {
          tag
        }
        status
        uploadProgress
      }
      hasMore
    }
  }
`;

export const EDSHOP_VIDEO = gql`
  query EdshopifyVideo($where: EdshopifyVideoWhereInput!) {
    edshopifyVideo(where: $where) {
      id
      url
      name
      duration
      thumbnail
      description
      tags {
        tag
      }
      status
      uploadProgress
    }
  }
`;

export const SUBJECT_TOPICS = gql`
  query Products($where: ProductWhereInput!) {
    products(where: $where) {
      id
      productName
      productContent {
        id
        dynoList(orderBy: {listIndex: asc}) {
          id
          listName
        }
      }
    }
  }
`;

export const TIMES_REDEEMED_COUPON = gql`
  query EdshopifyTimesRedeemedCoupon($where: EdshopifyTimesRedeemedCouponWhereInput!) {
    edshopifyTimesRedeemedCoupon(where: $where) {
      coupons {
        id
        times_redeemed
      }
    }
  }
`;
export const TOKEN_USAGE = gql`
  query EdShopifyTokenUsageSummary($where: EdshopifyWhereInput!) {
    edShopifyTokenUsageSummary(where: $where) {
      creditsUsed
      creditLimit
      extraCredits
      totalCredits
      renewalDate
    }
  }
`;
